import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { useJudulHalaman } from "../../components/etams/Breadcrumb";
import { useAsetMeta, label, tone, labelPengingat, labelPengingatLaporan } from "../../hooks/useAsetMeta";
import {
  getAset, deleteAset, ubahStatusAset, uploadNotaAset, getNotaAsetUrl, uploadNotaLog,
  nonaktifkanJadwal, aktifkanJadwal, deleteJadwal,
  listLogAset, getNotaLogUrl, listLaporanAset, listKmAset, apiErrorMessage,
} from "../../lib/asetApi";
import { ASET_KELOLA_ROLES, ASET_NAV_ROLES } from "../../lib/roles";
import { formatDate, formatIDR } from "../../lib/format";
import { ArrowLeft, Pencil, Plus, RefreshCw, Trash2, ExternalLink, Upload, Power, PowerOff, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import AsetFormDialog from "./AsetFormDialog";
import JadwalFormDialog from "./JadwalFormDialog";
import CatatServisDialog from "./CatatServisDialog";
import KmFormDialog from "./KmFormDialog";
import LaporanFormDialog from "./LaporanFormDialog";
import { useNamaKaryawan, useRegions, useStores, namaKaryawan, formatKm, periodeLabel } from "./bantuAset";

// Detail Aset (L3a, 4b-1) — satu halaman bertab: Jadwal · Riwayat Servis ·
// Laporan Bulanan · Pembacaan Km (kendaraan saja). 4b-2 menambah Catat Servis
// (+ unggah nota log sekali tulis) dan Catat Km (MANUAL; KOREKSI hanya KC/OWNER);
// form laporan bulanan lahir di 4b-3.
//
// Wewenang tombol cermin backend (`aset_service`):
//   * ubah aset, ubah status, jadwal buat/ubah/nonaktif/aktif, nota aset
//     = ASET_KELOLA_ROLES (OWNER + KEPALA_CABANG region aset)
//   * hapus aset/jadwal = OWNER saja, hanya tanpa riwayat (L9/R12) — 409
//     backend ditampilkan verbatim
//   * status hanya lewat POST /status (alasan wajib di layar); is_active
//     jadwal hanya lewat /nonaktif (alasan wajib, R1) dan /aktif (R7)
// L4–L6 + S1: seluruh label/rona dari meta. Nol label ditulis di sini.

function Baris({ k, v, testId }) {
  return (
    <div className="flex gap-2 text-sm" data-testid={testId}>
      <span className="w-32 shrink-0 text-muted-foreground">{k}</span>
      <span className="min-w-0 break-words">{v ?? "—"}</span>
    </div>
  );
}

export default function DetailAset() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { roleCode, isOwner } = useAuth();
  const canKelola = ASET_KELOLA_ROLES.includes(roleCode);
  // Catat servis + km MANUAL = PERAN_CATAT (cermin ASET_NAV_ROLES); backend `wajib_catat` tetap pagarnya.
  const canCatat = ASET_NAV_ROLES.includes(roleCode);
  const { meta } = useAsetMeta();

  const [data, setData] = useState(null);
  const [memuat, setMemuat] = useState(true);
  const [galat, setGalat] = useState(null);
  const [tab, setTab] = useState("jadwal");

  const [log, setLog] = useState(null);
  const [laporan, setLaporan] = useState(null);       // {items,total,limit,offset}
  const [km, setKm] = useState(null);
  const [notaUrl, setNotaUrl] = useState(null);

  const [formAset, setFormAset] = useState(false);
  const [formJadwal, setFormJadwal] = useState(null);  // null tutup | {jadwal:null|obj}
  const [dialogStatus, setDialogStatus] = useState(false);
  const [dialogNonaktif, setDialogNonaktif] = useState(null);
  const [dialogAktif, setDialogAktif] = useState(null);
  const [hapusAset, setHapusAset] = useState(false);
  const [hapusJadwal, setHapusJadwal] = useState(null);
  const [dialogServis, setDialogServis] = useState(false);
  const [dialogKm, setDialogKm] = useState(false);
  const [dialogLaporan, setDialogLaporan] = useState(null);   // baris view yang dilapor
  const [notaLogSibuk, setNotaLogSibuk] = useState(null);
  const notaLogRef = useRef(null);

  const aset = data?.aset;
  useJudulHalaman(aset?.kode);
  const kendaraan = aset?.kategori === "KENDARAAN_BERMOTOR";
  const regions = useRegions(true);
  const stores = useStores();

  const muat = useCallback(async () => {
    setMemuat(true);
    setGalat(null);
    try {
      const r = await getAset(id);
      setData(r.data);
    } catch (e) {
      setGalat(apiErrorMessage(e, "Gagal memuat aset."));
    } finally {
      setMemuat(false);
    }
  }, [id]);
  useEffect(() => { muat(); }, [muat]);

  const muatLog = useCallback(async () => {
    try { setLog((await listLogAset(id)).data?.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat riwayat servis.")); setLog([]); }
  }, [id]);
  const muatLaporan = useCallback(async (offset = 0) => {
    try {
      const r = (await listLaporanAset(id, { offset })).data;
      setLaporan((lama) => (offset > 0 && lama ? { ...r, items: [...lama.items, ...(r.items || [])] } : r));
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat laporan bulanan.")); setLaporan({ items: [], total: 0 }); }
  }, [id]);
  const muatKm = useCallback(async () => {
    try { setKm((await listKmAset(id)).data?.items || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal memuat pembacaan km.")); setKm([]); }
  }, [id]);

  // Tab riwayat dimuat saat dibuka; km ikut dimuat lebih awal untuk kendaraan
  // (bawaan km_awal jadwal KILOMETER = odometer terakhir).
  useEffect(() => { if (tab === "servis" && log === null) muatLog(); }, [tab, log, muatLog]);
  useEffect(() => { if (tab === "laporan" && laporan === null) muatLaporan(0); }, [tab, laporan, muatLaporan]);
  useEffect(() => { if (kendaraan && km === null) muatKm(); }, [kendaraan, km, muatKm]);
  useEffect(() => { if (tab === "km" && km === null) muatKm(); }, [tab, km, muatKm]);

  useEffect(() => {
    let hidup = true;
    if (aset?.dokumen_nota_path) {
      getNotaAsetUrl(id).then((r) => { if (hidup) setNotaUrl(r.data?.signed_url || null); }).catch(() => {});
    } else {
      setNotaUrl(null);
    }
    return () => { hidup = false; };
  }, [id, aset?.dokumen_nota_path]);

  const segarkanSemua = () => { setLog(null); setLaporan(null); setKm(null); muat(); };

  // Jadwal (tabel, termasuk nonaktif) digabung dengan baris view (aktif saja)
  // supaya satu baris membawa jatuh tempo + status pengingat.
  const jadwalGabung = useMemo(() => {
    const view = new Map((data?.jatuh_tempo || []).map((v) => [v.jadwal_id, v]));
    return (data?.jadwal || []).map((j) => ({ ...j, view: view.get(j.id) || null }));
  }, [data]);
  const kmTerakhir = useMemo(() => (km && km.length > 0 ? km[0].km : null), [km]);
  // Nama karyawan HANYA untuk id yang tampil di layar ini (satu panggilan `?ids=`), bukan seluruh karyawan
  // region (pola daftar seragam, 28 Sep 2026). Pemilih karyawan di dialog mencari sendiri ke server.
  const semuaKaryawan = useNamaKaryawan([
    aset?.pic_employee_id,
    ...jadwalGabung.map((j) => j.pic_employee_id),
    ...(log || []).map((l) => l.dilaksanakan_oleh),
    ...((laporan && laporan.items) || []).map((l) => l.pelapor_employee_id),
    ...(km || []).map((k) => k.pembaca_employee_id),
    dialogLaporan?.pic_employee_id,
  ]);
  // "Hari ini" zona region dari baris view (bukan jam browser): jadwal aktif
  // atau status laporan (ada untuk tiap aset yang belum keluar).
  const hariIniRegionView = data?.jatuh_tempo?.[0]?.tanggal_hari_ini_region
    || data?.laporan_status?.[0]?.tanggal_hari_ini_region || null;

  const unggahNotaLog = async (e) => {
    const f = e.target.files?.[0];
    const logId = notaLogSibuk;
    if (!f || !logId) return;
    try {
      await uploadNotaLog(logId, f);
      toast.success("Nota terunggah.");
      muatLog();
    } catch (err) { toast.error(apiErrorMessage(err, "Gagal mengunggah nota.")); }
    finally { setNotaLogSibuk(null); if (notaLogRef.current) notaLogRef.current.value = ""; }
  };

  const bukaNotaLog = async (logId) => {
    try {
      const r = await getNotaLogUrl(logId);
      if (r.data?.signed_url) window.open(r.data.signed_url, "_blank", "noopener");
      else toast.message("Catatan servis ini tidak punya nota.");
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal membuka nota.")); }
  };

  if (memuat && !data) return <div className="p-8 text-sm text-muted-foreground" data-testid="aset-detail-memuat">Memuat...</div>;
  if (galat) {
    return (
      <div className="space-y-4" data-testid="aset-detail-galat">
        <Button variant="ghost" size="sm" onClick={() => navigate("/aset")}><ArrowLeft size={16} className="mr-1" /> Daftar Aset</Button>
        <Card className="p-6 text-sm text-danger-soft-foreground bg-danger-soft">{galat}</Card>
      </div>
    );
  }
  if (!aset) return null;

  const ketentuanJadwal = (j) => {
    if (j.bentuk_jadwal === "TANGGAL") return formatDate(j.tanggal_target);
    if (j.bentuk_jadwal === "PERIODE") return `Setiap ${j.interval_nilai} ${label(meta, "interval_satuan", j.interval_satuan)} sejak ${formatDate(j.tanggal_mulai)}`;
    if (j.bentuk_jadwal === "KILOMETER") return `Setiap ${formatKm(j.interval_km)} dari ${formatKm(j.km_awal)}`;
    return "—";
  };
  const jatuhTempo = (j) => {
    if (!j.view) return <span className="text-muted-foreground">—</span>;
    return j.bentuk_jadwal === "KILOMETER"
      ? `${formatKm(j.view.km_jatuh_tempo)}${j.view.km_terakhir != null ? ` (terbaca ${formatKm(j.view.km_terakhir)})` : ""}`
      : formatDate(j.view.tanggal_jatuh_tempo);
  };

  return (
    <div className="space-y-6" data-testid="aset-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <Link to="/aset" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Daftar Aset
          </Link>
          <h1 className="text-2xl font-semibold flex items-center gap-2 flex-wrap mt-1">
            <span className="font-mono text-lg" data-testid="aset-detail-kode">{aset.kode}</span>
            <span data-testid="aset-detail-nama">{aset.nama}</span>
            <StatusBadge tone={tone(meta, "status_aset", aset.status)} testId="aset-detail-status">
              {label(meta, "status_aset", aset.status)}
            </StatusBadge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {label(meta, "kategori", aset.kategori)}{aset.jenis ? ` · ${aset.jenis}` : ""}{aset.nomor_seri ? ` · ${aset.nomor_seri}` : ""}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={segarkanSemua} data-testid="aset-detail-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Muat ulang</Button>
          {canKelola && (
            <>
              <Button variant="outline" size="sm" onClick={() => setFormAset(true)} data-testid="aset-ubah-btn"><Pencil className="h-4 w-4 mr-1" /> Ubah</Button>
              <Button variant="outline" size="sm" onClick={() => setDialogStatus(true)} data-testid="aset-ubah-status-btn"><ShieldAlert className="h-4 w-4 mr-1" /> Ubah Status</Button>
            </>
          )}
          {isOwner && (
            <Button variant="outline" size="sm" className="text-danger" onClick={() => setHapusAset(true)} data-testid="aset-hapus-btn"><Trash2 className="h-4 w-4 mr-1" /> Hapus</Button>
          )}
        </div>
      </div>

      <Card className="p-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2" data-testid="aset-detail-info">
        <Baris k="Wilayah" v={regions.find((r) => r.id === aset.region_id)?.name || "—"} testId="aset-detail-region" />
        <Baris k="PIC" v={namaKaryawan(semuaKaryawan, aset.pic_employee_id)} testId="aset-detail-pic" />
        <Baris k="Cabang" v={aset.store_id ? (stores.find((s) => s.id === aset.store_id)?.name || "—") : "—"} />
        <Baris k="Tanggal Beli" v={formatDate(aset.tanggal_beli)} />
        <Baris k="Harga Beli" v={aset.harga_beli_idr != null ? formatIDR(aset.harga_beli_idr) : "—"} />
        <Baris k="Vendor" v={aset.vendor || "—"} />
        <Baris k="Catatan" v={aset.catatan || "—"} />
        <div className="flex gap-2 text-sm items-center">
          <span className="w-32 shrink-0 text-muted-foreground">Nota Pembelian</span>
          {notaUrl ? (
            <a href={notaUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 underline underline-offset-2" data-testid="aset-nota-link">
              <ExternalLink size={14} /> Buka nota
            </a>
          ) : <span className="text-muted-foreground">—</span>}
          {canKelola && <NotaAsetUploader asetId={aset.id} onSelesai={muat} />}
        </div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="aset-tabs">
          <TabsTrigger value="jadwal" data-testid="aset-tab-jadwal">Jadwal</TabsTrigger>
          <TabsTrigger value="servis" data-testid="aset-tab-servis">Riwayat Servis</TabsTrigger>
          <TabsTrigger value="laporan" data-testid="aset-tab-laporan">Laporan Bulanan</TabsTrigger>
          {kendaraan && <TabsTrigger value="km" data-testid="aset-tab-km">Pembacaan Km</TabsTrigger>}
        </TabsList>

        {/* ---------------- Jadwal ---------------- */}
        <TabsContent value="jadwal">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">{jadwalGabung.length} jadwal</span>
              {canKelola && (
                <Button size="sm" onClick={() => setFormJadwal({ jadwal: null })} data-testid="jadwal-create-btn"><Plus className="h-4 w-4 mr-1" /> Jadwal Baru</Button>
              )}
            </div>
            {jadwalGabung.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="jadwal-kosong">
                Belum ada jadwal maintenance untuk aset ini.{canKelola ? " Tekan \"Jadwal Baru\" supaya pengingat mulai berjalan." : ""}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Nama</th>
                      <th className="text-left px-3 py-2">Bentuk</th>
                      <th className="text-left px-3 py-2">Periode</th>
                      <th className="text-left px-3 py-2">Jatuh Tempo</th>
                      <th className="text-left px-3 py-2">Pengingat</th>
                      <th className="text-left px-3 py-2">Pengingat ke</th>
                      <th className="text-left px-3 py-2">Status Jadwal</th>
                      {canKelola && <th className="px-3 py-2" />}
                    </tr>
                  </thead>
                  <tbody>
                    {jadwalGabung.map((j) => (
                      <tr key={j.id} className={`border-t border-border ${j.is_active ? "" : "opacity-70"}`} data-testid={`jadwal-row-${j.id}`}>
                        <td className="px-3 py-2 font-medium">
                          {j.nama}
                          {j.pic_employee_id ? <span className="block text-xs text-muted-foreground">PIC: {namaKaryawan(semuaKaryawan, j.pic_employee_id)}</span> : null}
                        </td>
                        <td className="px-3 py-2">{label(meta, "bentuk_jadwal", j.bentuk_jadwal)}</td>
                        <td className="px-3 py-2 text-xs">{ketentuanJadwal(j)}</td>
                        <td className="px-3 py-2 text-xs">{jatuhTempo(j)}</td>
                        <td className="px-3 py-2">
                          {j.view ? (
                            <StatusBadge tone={tone(meta, "status_pengingat", j.view.status_pengingat)} testId={`jadwal-pengingat-${j.id}`}>
                              {labelPengingat(meta, j.view)}
                            </StatusBadge>
                          ) : <span className="text-muted-foreground text-xs">—</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">{label(meta, "peran_notifikasi", j.peran_notifikasi)}</td>
                        <td className="px-3 py-2 text-xs">
                          {j.is_active ? (
                            <StatusBadge tone="success" testId={`jadwal-aktif-${j.id}`}>Aktif</StatusBadge>
                          ) : (
                            <div>
                              <StatusBadge tone="neutral" testId={`jadwal-nonaktif-${j.id}`}>Nonaktif</StatusBadge>
                              <div className="text-muted-foreground mt-1">
                                {label(meta, "nonaktif_sumber", j.nonaktif_sumber)}
                                {j.nonaktif_alasan ? ` — ${j.nonaktif_alasan}` : ""}
                                {j.nonaktif_at ? ` (${formatDate(j.nonaktif_at)})` : ""}
                              </div>
                            </div>
                          )}
                        </td>
                        {canKelola && (
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            <Button variant="ghost" size="sm" title="Ubah jadwal" onClick={() => setFormJadwal({ jadwal: j })} data-testid={`jadwal-ubah-${j.id}`}><Pencil className="h-4 w-4" /></Button>
                            {j.is_active ? (
                              <Button variant="ghost" size="sm" title="Nonaktifkan" onClick={() => setDialogNonaktif(j)} data-testid={`jadwal-nonaktif-btn-${j.id}`}><PowerOff className="h-4 w-4" /></Button>
                            ) : (
                              <Button variant="ghost" size="sm" title="Aktifkan lagi" onClick={() => setDialogAktif(j)} data-testid={`jadwal-aktif-btn-${j.id}`}><Power className="h-4 w-4" /></Button>
                            )}
                            {isOwner && (
                              <Button variant="ghost" size="sm" title="Hapus jadwal" className="text-danger" onClick={() => setHapusJadwal(j)} data-testid={`jadwal-hapus-${j.id}`}><Trash2 className="h-4 w-4" /></Button>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------------- Riwayat Servis (+ Catat Servis, 4b-2) ---------------- */}
        <TabsContent value="servis">
          <Card className="p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-muted-foreground">{log ? `${log.length} catatan servis` : ""}</span>
              {canCatat && (
                <Button size="sm" onClick={() => setDialogServis(true)} data-testid="servis-create-btn"><Plus className="h-4 w-4 mr-1" /> Catat Servis</Button>
              )}
            </div>
            <input ref={notaLogRef} type="file" className="hidden" onChange={unggahNotaLog} data-testid="log-nota-input" />
            {log === null ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Memuat...</div>
            ) : log.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground" data-testid="log-kosong">Belum ada catatan servis untuk aset ini.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Tanggal Lapor</th>
                      <th className="text-left px-3 py-2">Dilaksanakan</th>
                      <th className="text-left px-3 py-2">Jenis</th>
                      <th className="text-left px-3 py-2">Jadwal</th>
                      <th className="text-left px-3 py-2">Hasil</th>
                      <th className="text-left px-3 py-2">Kondisi Setelah</th>
                      <th className="text-left px-3 py-2">Oleh</th>
                      <th className="text-right px-3 py-2">Biaya</th>
                      {kendaraan && <th className="text-right px-3 py-2">Km</th>}
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {log.map((l) => (
                      <tr key={l.id} className="border-t border-border align-top" data-testid={`log-row-${l.id}`}>
                        <td className="px-3 py-2">{formatDate(l.tanggal_lapor)}</td>
                        <td className="px-3 py-2">{formatDate(l.tanggal_pelaksanaan)}</td>
                        <td className="px-3 py-2">{label(meta, "jenis_log", l.jenis)}</td>
                        <td className="px-3 py-2 text-xs">{jadwalGabung.find((j) => j.id === l.jadwal_id)?.nama || <span className="text-muted-foreground">—</span>}</td>
                        <td className="px-3 py-2">
                          {l.hasil ? <StatusBadge tone={tone(meta, "hasil_log", l.hasil)}>{label(meta, "hasil_log", l.hasil)}</StatusBadge> : "—"}
                        </td>
                        <td className="px-3 py-2">
                          {l.kondisi_setelah ? <StatusBadge tone={tone(meta, "kondisi", l.kondisi_setelah)}>{label(meta, "kondisi", l.kondisi_setelah)}</StatusBadge> : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs">
                          {l.dilaksanakan_oleh ? namaKaryawan(semuaKaryawan, l.dilaksanakan_oleh) : (l.vendor_teknisi || "—")}
                          {l.keluhan ? <span className="block text-muted-foreground">Keluhan: {l.keluhan}</span> : null}
                          {l.catatan ? <span className="block text-muted-foreground">{l.catatan}</span> : null}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">{l.biaya_idr != null ? formatIDR(l.biaya_idr) : "—"}</td>
                        {kendaraan && <td className="px-3 py-2 text-right tabular-nums">{l.km != null ? formatKm(l.km) : "—"}</td>}
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {l.dokumen_nota_path ? (
                            <Button variant="ghost" size="sm" title="Buka nota" onClick={() => bukaNotaLog(l.id)} data-testid={`log-nota-${l.id}`}><ExternalLink className="h-4 w-4" /></Button>
                          ) : canCatat ? (
                            // Sekali tulis (R9): tombol hilang begitu nota ada.
                            <Button variant="ghost" size="sm" title="Unggah nota" disabled={!!notaLogSibuk}
                                    onClick={() => { setNotaLogSibuk(l.id); notaLogRef.current?.click(); }} data-testid={`log-nota-unggah-${l.id}`}>
                              <Upload className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* ---------------- Laporan Bulanan (+ Lapor, 4b-3) ---------------- */}
        <TabsContent value="laporan">
          <Card className="p-4 space-y-4">
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Kewajiban lapor</div>
              {(data?.laporan_status || []).length === 0 ? (
                <div className="text-sm text-muted-foreground" data-testid="laporan-status-kosong">Aset ini tidak wajib laporan bulanan (sudah keluar dari cabang).</div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {(data?.laporan_status || []).map((s) => (
                    <div key={s.periode} className="rounded border border-border px-3 py-2 text-sm flex items-center gap-2" data-testid={`laporan-status-${s.periode}`}>
                      <span className="font-medium">{periodeLabel(s.periode)}</span>
                      <StatusBadge tone={tone(meta, "status_pengingat_laporan", s.status_pengingat)}>{labelPengingatLaporan(meta, s)}</StatusBadge>
                      {s.tanggal_lapor ? <span className="text-xs text-muted-foreground">dilaporkan {formatDate(s.tanggal_lapor)}</span> : null}
                      {canCatat && !s.sudah_lapor && (
                        <Button size="sm" variant="outline" onClick={() => setDialogLaporan(s)} data-testid={`laporan-lapor-${s.periode}`}>Lapor</Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground mb-2">Riwayat</div>
              {laporan === null ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Memuat...</div>
              ) : laporan.items.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground" data-testid="laporan-kosong">Belum ada laporan bulanan untuk aset ini.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Periode</th>
                        <th className="text-left px-3 py-2">Dilaporkan</th>
                        <th className="text-left px-3 py-2">Ketepatan</th>
                        <th className="text-left px-3 py-2">Keberadaan</th>
                        <th className="text-left px-3 py-2">Kondisi Fisik</th>
                        <th className="text-left px-3 py-2">Pelapor</th>
                        <th className="text-left px-3 py-2">Catatan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {laporan.items.map((l) => (
                        <tr key={l.id} className="border-t border-border align-top" data-testid={`laporan-row-${l.id}`}>
                          <td className="px-3 py-2 font-medium">{periodeLabel(l.periode)}</td>
                          <td className="px-3 py-2">{formatDate(l.tanggal_lapor)}</td>
                          <td className="px-3 py-2"><StatusBadge tone={tone(meta, "status_laporan", l.status_laporan)}>{label(meta, "status_laporan", l.status_laporan)}</StatusBadge></td>
                          <td className="px-3 py-2">
                            <StatusBadge tone={tone(meta, "status_keberadaan", l.status_keberadaan)}>{label(meta, "status_keberadaan", l.status_keberadaan)}</StatusBadge>
                            {l.lokasi_baru ? <span className="block text-xs text-muted-foreground">{l.lokasi_baru}</span> : null}
                          </td>
                          <td className="px-3 py-2"><StatusBadge tone={tone(meta, "kondisi", l.kondisi_fisik)}>{label(meta, "kondisi", l.kondisi_fisik)}</StatusBadge></td>
                          <td className="px-3 py-2 text-xs">{namaKaryawan(semuaKaryawan, l.pelapor_employee_id)}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{l.catatan || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {laporan.total > laporan.items.length && (
                    <div className="px-3 py-2 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
                      <span className="tabular-nums">{laporan.items.length} dari {laporan.total}</span>
                      <Button size="sm" variant="outline" onClick={() => muatLaporan(laporan.items.length)} data-testid="laporan-muat-lagi">Muat lebih banyak</Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </TabsContent>

        {/* ---------------- Pembacaan Km (+ Catat Km, 4b-2; kendaraan) ---------------- */}
        {kendaraan && (
          <TabsContent value="km">
            <Card className="p-4 space-y-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">Odometer terakhir: <span className="font-medium tabular-nums" data-testid="km-terakhir">{formatKm(kmTerakhir)}</span></div>
                {canCatat && (
                  <Button size="sm" onClick={() => setDialogKm(true)} data-testid="km-create-btn"><Plus className="h-4 w-4 mr-1" /> Catat Pembacaan Km</Button>
                )}
              </div>
              {km === null ? (
                <div className="py-6 text-center text-sm text-muted-foreground">Memuat...</div>
              ) : km.length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground" data-testid="km-kosong">Belum ada pembacaan km untuk kendaraan ini.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-muted-foreground text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Tanggal Baca</th>
                        <th className="text-right px-3 py-2">Km</th>
                        <th className="text-left px-3 py-2">Jenis Pembacaan</th>
                        <th className="text-left px-3 py-2">Pembaca</th>
                        <th className="text-left px-3 py-2">Catatan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {km.map((k) => (
                        <tr key={k.id} className="border-t border-border" data-testid={`km-row-${k.id}`}>
                          <td className="px-3 py-2">{formatDate(k.tanggal_baca)}</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatKm(k.km)}
                            {k.km_odometer_lama != null ? <span className="block text-xs text-muted-foreground">sebelumnya {formatKm(k.km_odometer_lama)}</span> : null}
                          </td>
                          <td className="px-3 py-2">{label(meta, "sumber_km", k.sumber)}</td>
                          <td className="px-3 py-2 text-xs">{namaKaryawan(semuaKaryawan, k.pembaca_employee_id)}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground">{k.catatan || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* ---------------- dialog ---------------- */}
      <AsetFormDialog terbuka={formAset} onTutup={() => setFormAset(false)}
                      onTersimpan={() => { setFormAset(false); muat(); }}
                      meta={meta} aset={aset} isOwner={isOwner} regionProfil={aset.region_id}
                      regionNama={regions.find((r) => r.id === aset.region_id)?.name || null} />
      <JadwalFormDialog terbuka={!!formJadwal} onTutup={() => setFormJadwal(null)}
                        onTersimpan={() => { setFormJadwal(null); muat(); }}
                        meta={meta} aset={aset} jadwal={formJadwal?.jadwal || null}
                        kmTerakhir={kmTerakhir} />
      <CatatServisDialog terbuka={dialogServis} onTutup={() => setDialogServis(false)}
                         onSelesai={() => { setDialogServis(false); setLog(null); setKm(null); muat(); }}
                         meta={meta} aset={aset} jadwalAktif={jadwalGabung} jadwalId={null}
                         jenisBawaan="CORRECTIVE" hariIni={hariIniRegionView} />
      <LaporanFormDialog terbuka={!!dialogLaporan} onTutup={() => setDialogLaporan(null)}
                         onSelesai={() => { setDialogLaporan(null); setLaporan(null); setKm(null); muat(); }}
                         meta={meta} baris={dialogLaporan}
                         namaPic={dialogLaporan ? namaKaryawan(semuaKaryawan, dialogLaporan.pic_employee_id) : null} />
      <KmFormDialog terbuka={dialogKm} onTutup={() => setDialogKm(false)}
                    onSelesai={() => { setDialogKm(false); setKm(null); muat(); }}
                    meta={meta} aset={aset} kmTerakhir={kmTerakhir}
                    hariIni={hariIniRegionView} bolehKoreksi={canKelola} />
      <StatusDialog terbuka={dialogStatus} onTutup={() => setDialogStatus(false)} meta={meta} aset={aset}
                    onSelesai={() => { setDialogStatus(false); muat(); }} />
      <NonaktifDialog jadwal={dialogNonaktif} onTutup={() => setDialogNonaktif(null)}
                      onSelesai={() => { setDialogNonaktif(null); muat(); }} />
      <AktifkanDialog jadwal={dialogAktif} meta={meta} onTutup={() => setDialogAktif(null)}
                      onSelesai={() => { setDialogAktif(null); muat(); }} />
      <HapusDialog terbuka={hapusAset} judul="Hapus Aset" onTutup={() => setHapusAset(false)}
                   keterangan={<>Hapus aset <span className="font-mono text-xs">{aset.kode}</span> {aset.nama}? Aset yang sudah punya jadwal, catatan servis, laporan, atau pembacaan km tidak bisa dihapus — ubah statusnya saja.</>}
                   jalankan={async () => { await deleteAset(aset.id); toast.success("Aset dihapus."); navigate("/aset"); }}
                   testId="aset-hapus-dialog" />
      <HapusDialog terbuka={!!hapusJadwal} judul="Hapus Jadwal" onTutup={() => setHapusJadwal(null)}
                   keterangan={<>Hapus jadwal <span className="font-medium">{hapusJadwal?.nama}</span>? Jadwal yang sudah punya catatan servis tidak bisa dihapus — nonaktifkan saja.</>}
                   jalankan={async () => { await deleteJadwal(hapusJadwal.id); toast.success("Jadwal dihapus."); setHapusJadwal(null); muat(); }}
                   testId="jadwal-hapus-dialog" />
    </div>
  );
}


// --------------------------------------------------------------------------
// Ubah status — hanya lewat POST /{id}/status. Alasan WAJIB untuk SEMUA
// perubahan status = VONIS OWNER 16 Sep 2026 (sengaja lebih ketat dari backend,
// yang mewajibkannya hanya untuk status keluar, R2; status FINAL hanya OWNER, R7).
// --------------------------------------------------------------------------
function StatusDialog({ terbuka, onTutup, onSelesai, meta, aset }) {
  const [status, setStatus] = useState("");
  const [alasan, setAlasan] = useState("");
  const [sibuk, setSibuk] = useState(false);
  useEffect(() => { if (terbuka) { setStatus(""); setAlasan(""); } }, [terbuka]);
  const kirim = async () => {
    if (!status) { toast.error("Status baru wajib dipilih."); return; }
    if (!alasan.trim()) { toast.error("Alasan wajib diisi."); return; }
    setSibuk(true);
    try {
      const r = await ubahStatusAset(aset.id, { status_baru: status, alasan: alasan.trim() });
      const n = r.data?.jadwal_dinonaktifkan;
      toast.success(`Status diubah ke ${label(meta, "status_aset", status)}.${Array.isArray(n) && n.length ? ` ${n.length} jadwal dinonaktifkan.` : ""}`);
      onSelesai();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengubah status."));
    } finally { setSibuk(false); }
  };
  return (
    <Dialog open={terbuka} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent data-testid="aset-status-dialog">
        <DialogHeader><DialogTitle>Ubah Status Aset</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">Status sekarang: <StatusBadge tone={tone(meta, "status_aset", aset?.status)}>{label(meta, "status_aset", aset?.status)}</StatusBadge></div>
          <div>
            <Label>Status Baru *</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="aset-status-pilih"><SelectValue placeholder="Pilih status" /></SelectTrigger>
              <SelectContent>
                {(meta?.status_aset || []).filter((s) => s.code !== aset?.status).map((s) => <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Alasan *</Label>
            <Textarea rows={3} value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid="aset-status-alasan" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={sibuk}>Batal</Button>
          <BusyButton onClick={kirim} busy={sibuk} busyText="Menyimpan..." data-testid="aset-status-simpan">Simpan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R1: alasan wajib. TERLAMBAT/KM_BASI hanya OWNER (R6) — 403 backend verbatim.
function NonaktifDialog({ jadwal, onTutup, onSelesai }) {
  const [alasan, setAlasan] = useState("");
  const [sibuk, setSibuk] = useState(false);
  useEffect(() => { if (jadwal) setAlasan(""); }, [jadwal]);
  const kirim = async () => {
    if (!alasan.trim()) { toast.error("Alasan wajib diisi."); return; }
    setSibuk(true);
    try {
      await nonaktifkanJadwal(jadwal.id, { alasan: alasan.trim() });
      toast.success("Jadwal dinonaktifkan.");
      onSelesai();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal menonaktifkan jadwal.")); }
    finally { setSibuk(false); }
  };
  return (
    <Dialog open={!!jadwal} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent data-testid="jadwal-nonaktif-dialog">
        <DialogHeader><DialogTitle>Nonaktifkan Jadwal</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Jadwal <span className="font-medium text-foreground">{jadwal?.nama}</span> berhenti mengirim pengingat sampai diaktifkan lagi.</p>
        <div>
          <Label>Alasan *</Label>
          <Textarea rows={3} value={alasan} onChange={(e) => setAlasan(e.target.value)} data-testid="jadwal-nonaktif-alasan" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={sibuk}>Batal</Button>
          <BusyButton onClick={kirim} busy={sibuk} busyText="Menyimpan..." data-testid="jadwal-nonaktif-simpan">Nonaktifkan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// R7: aktif ulang HANYA lewat RPC; jangkar opsional sesuai bentuk.
function AktifkanDialog({ jadwal, meta, onTutup, onSelesai }) {
  const [nilai, setNilai] = useState("");
  const [sibuk, setSibuk] = useState(false);
  useEffect(() => { if (jadwal) setNilai(""); }, [jadwal]);
  const bentuk = jadwal?.bentuk_jadwal;
  const kirim = async () => {
    setSibuk(true);
    try {
      const body = {};
      if (nilai !== "") {
        if (bentuk === "TANGGAL") body.tanggal_target = nilai;
        if (bentuk === "PERIODE") body.tanggal_mulai = nilai;
        if (bentuk === "KILOMETER") body.km_awal = Number(nilai);
      }
      const r = await aktifkanJadwal(jadwal.id, body);
      toast.success(`Jadwal diaktifkan.${r.data?.status_pengingat_sesudah ? ` Status: ${label(meta, "status_pengingat", r.data.status_pengingat_sesudah)}.` : ""}`);
      onSelesai();
    } catch (e) { toast.error(apiErrorMessage(e, "Gagal mengaktifkan jadwal.")); }
    finally { setSibuk(false); }
  };
  const labelJangkar = bentuk === "TANGGAL" ? "Tanggal target baru" : bentuk === "PERIODE" ? "Dihitung sejak" : "Km saat ini";
  return (
    <Dialog open={!!jadwal} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent data-testid="jadwal-aktif-dialog">
        <DialogHeader><DialogTitle>Aktifkan Jadwal</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">
          Jadwal <span className="font-medium text-foreground">{jadwal?.nama}</span> ({label(meta, "bentuk_jadwal", bentuk)}) akan mengirim pengingat lagi.
          {jadwal?.nonaktif_sumber ? ` Dinonaktifkan: ${label(meta, "nonaktif_sumber", jadwal.nonaktif_sumber)}${jadwal.nonaktif_alasan ? ` — ${jadwal.nonaktif_alasan}` : ""}.` : ""}
        </p>
        <div>
          <Label>{labelJangkar} (opsional)</Label>
          <Input type={bentuk === "KILOMETER" ? "number" : "date"} min="0" value={nilai} onChange={(e) => setNilai(e.target.value)} data-testid="jadwal-aktif-jangkar" />
          <div className="text-xs text-muted-foreground mt-1">Kosong = memakai ketentuan jadwal yang lama.</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={sibuk}>Batal</Button>
          <BusyButton onClick={kirim} busy={sibuk} busyText="Menyimpan..." data-testid="jadwal-aktif-simpan">Aktifkan</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// L9/R12: OWNER saja; 409 backend (ASET_PUNYA_RIWAYAT / JADWAL_PUNYA_LOG) verbatim.
function HapusDialog({ terbuka, judul, keterangan, jalankan, onTutup, testId }) {
  const [sibuk, setSibuk] = useState(false);
  const kirim = async () => {
    setSibuk(true);
    try { await jalankan(); }
    catch (e) { toast.error(apiErrorMessage(e, "Gagal menghapus.")); }
    finally { setSibuk(false); }
  };
  return (
    <Dialog open={terbuka} onOpenChange={(o) => { if (!o) onTutup(); }}>
      <DialogContent data-testid={testId}>
        <DialogHeader><DialogTitle>{judul}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">{keterangan}</p>
        <DialogFooter>
          <Button variant="outline" onClick={onTutup} disabled={sibuk}>Batal</Button>
          <BusyButton variant="destructive" onClick={kirim} busy={sibuk} busyText="Menghapus..." data-testid={`${testId}-confirm`}>Hapus</BusyButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Nota pembelian aset — POST /{id}/nota (OWNER/KC). Batas MIME/ukuran hidup di
// backend (`NOTA_MIME`, `NOTA_MAKS_BYTE`) dan meta nol memuatnya; pesan
// 415/413 backend ditampilkan verbatim, nol konstanta disalin ke sini.
function NotaAsetUploader({ asetId, onSelesai }) {
  const ref = useRef(null);
  const [sibuk, setSibuk] = useState(false);
  const pilih = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setSibuk(true);
    try {
      await uploadNotaAset(asetId, f);
      toast.success("Nota terunggah.");
      onSelesai();
    } catch (err) { toast.error(apiErrorMessage(err, "Gagal mengunggah nota.")); }
    finally { setSibuk(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" className="hidden" onChange={pilih} data-testid="aset-nota-input" />
      <BusyButton variant="ghost" size="sm" busy={sibuk} busyText="Mengunggah..." onClick={() => ref.current?.click()} data-testid="aset-nota-unggah">
        <Upload className="h-4 w-4 mr-1" /> Unggah nota
      </BusyButton>
    </>
  );
}
